/**
 * Semantic Match Service -- Parse Functions + Types Only
 * AI call logic da chuyen sang Workers API (handlers/semanticMatch.ts)
 * File nay chi giu lai parse functions, types, va utility functions cho frontend dung
 */

import type { UsageStats } from '../shared/types';

// ============================================
// INTERFACES
// ============================================

export interface SegmentMapping {
    sourceIndex: number;      // 0-based index of VI segment
    humanIndex: number;       // Primary EN index (first match), -1 if no match
    allHumanIndices: number[]; // All matched EN indices (0-based)
    confidence: number;       // 0-100
    sourceText: string;       // Original VI text
    humanText: string;        // Combined EN text (joined if multi-match)
    isUnmatched: boolean;     // True if no match found
}

export interface SemanticMatchResult {
    mappings: SegmentMapping[];
    alignedHumanSegments: string[]; // Reordered EN segments matching VI order
    rawOutput: string;              // Raw AI response for debugging
    unmatchedCount: number;         // Number of VI segments with no match
    usage?: UsageStats;             // Token usage & cost
}

// ============================================
// PARSE FUNCTIONS
// ============================================

/**
 * Parse AI output into SegmentMapping array
 */
export const parseOutput = (output: string, viSegments: string[], enSegments: string[]): SegmentMapping[] => {
    const results: SegmentMapping[] = [];
    const lines = output.split('\n').filter(l => l.includes('|') && !l.includes('viIndex'));

    for (const line of lines) {
        const parts = line.split('|').map(p => p.trim());
        if (parts.length >= 3) {
            const sourceIndicesStr = parts[0].replace(/[^\d;]/g, '');
            const sourceIndices = sourceIndicesStr.split(';')
                .map(s => parseInt(s))
                .filter(n => !isNaN(n));

            const enIndicesStr = parts[1];
            const confidence = parseInt(parts[2].replace(/[^\d]/g, '')) || 0;

            for (const viIdxDisplay of sourceIndices) {
                const viLocalIndex = viIdxDisplay - 1;

                if (viLocalIndex >= 0 && viLocalIndex < viSegments.length) {
                    const enIndices = enIndicesStr.split(';')
                        .map(s => parseInt(s.replace(/[^\d]/g, '')) - 1)
                        .filter(n => !isNaN(n) && n >= 0 && n < enSegments.length);

                    const primaryEnIdx = enIndices[0] ?? -1;
                    const isUnmatched = primaryEnIdx < 0 || enIndicesStr.trim() === '0';

                    let humanText = '[NOT MATCHED]';
                    if (!isUnmatched && primaryEnIdx >= 0) {
                        humanText = enIndices
                            .map(idx => enSegments[idx])
                            .filter(t => t !== undefined)
                            .join(' ');
                    }

                    results.push({
                        sourceIndex: viLocalIndex,
                        humanIndex: primaryEnIdx,
                        allHumanIndices: isUnmatched ? [] : enIndices,
                        confidence: isUnmatched ? 0 : confidence,
                        sourceText: viSegments[viLocalIndex] || '',
                        humanText: humanText,
                        isUnmatched: isUnmatched
                    });
                }
            }
        }
    }

    // Fill in any missing VI segments
    for (let i = 0; i < viSegments.length; i++) {
        const existing = results.find(m => m.sourceIndex === i);
        if (!existing) {
            results.push({
                sourceIndex: i,
                humanIndex: -1,
                allHumanIndices: [],
                confidence: 0,
                sourceText: viSegments[i],
                humanText: '[NOT MATCHED]',
                isUnmatched: true
            });
        }
    }

    results.sort((a, b) => a.sourceIndex - b.sourceIndex);
    return results;
};

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Helper: Get display string for EN indices (e.g., "EN-1 +2" or "EN-1,3")
 */
export function getIndexDisplay(mapping: SegmentMapping): { display: string; tooltip: string } {
    if (mapping.isUnmatched || mapping.allHumanIndices.length === 0) {
        return { display: 'NO MATCH', tooltip: 'No matching EN segment found' };
    }

    const indices = mapping.allHumanIndices.map(i => i + 1); // 1-based

    const isSequential = indices.every((val, i, arr) => i === 0 || val === arr[i - 1] + 1);
    let tooltip = '';
    if (isSequential && indices.length > 1) {
        tooltip = `Matched: EN-${indices[0]} to EN-${indices[indices.length - 1]}`;
    } else {
        tooltip = `Matched: EN-${indices.join(', EN-')}`;
    }

    let display = '';
    if (indices.length === 1) {
        display = `EN-${indices[0]}`;
    } else {
        display = `EN-${indices[0]} +${indices.length - 1}`;
    }

    return { display, tooltip };
}
